/**
 * Date utilities using date-fns
 * Centralized date formatting and manipulation functions
 */

import {
  format,
  formatDistanceToNow,
  parseISO,
  isToday,
  isYesterday,
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  addDays,
  subDays,
  addMonths,
  subMonths,
  differenceInDays,
  isSameDay,
  isSameWeek,
  isSameMonth,
  isValid,
} from 'date-fns';

// Default format patterns
export const DATE_FORMATS = {
  SHORT: 'MM/dd/yyyy',
  MEDIUM: 'MMM d, yyyy',
  LONG: 'MMMM d, yyyy',
  FULL: 'EEEE, MMMM d, yyyy',
  TIME: 'h:mm a',
  DATETIME_SHORT: 'MM/dd/yyyy h:mm a',
  DATETIME_MEDIUM: 'MMM d, yyyy h:mm a',
  DATETIME_LONG: 'MMMM d, yyyy h:mm a',
  ISO: "yyyy-MM-dd'T'HH:mm:ss.SSSxxx",
  MONTH_YEAR: 'MMMM yyyy',
  YEAR: 'yyyy',
  WEEKDAY: 'EEEE',
} as const;

/**
 * Format a date or date string to a specific format
 */
export function formatDate(
  date: Date | string | null | undefined,
  formatPattern: string = DATE_FORMATS.MEDIUM,
): string
{
  if (!date) return '';

  const parsedDate = typeof date === 'string' ? parseISO(date) : date;

  if (!isValid(parsedDate)) return '';

  return format(parsedDate, formatPattern);
}

/**
 * Format a date as a relative time (e.g., "2 hours ago")
 */
export function formatRelativeTime(date: Date | string | null | undefined): string
{
  if (!date) return '';

  const parsedDate = typeof date === 'string' ? parseISO(date) : date;

  if (!isValid(parsedDate)) return '';

  return formatDistanceToNow(parsedDate, { addSuffix: true });
}

/**
 * Format a date with smart formatting (today, yesterday, or date)
 */
export function formatSmartDate(
  date: Date | string | null | undefined,
  includeTime: boolean = false,
): string
{
  if (!date) return '';

  const parsedDate = typeof date === 'string' ? parseISO(date) : date;

  if (!isValid(parsedDate)) return '';

  if (isToday(parsedDate))
  {
    return includeTime ? `Today, ${format(parsedDate, TIME_FORMATS.TIME)}` : 'Today';
  }

  if (isYesterday(parsedDate))
  {
    return includeTime ? `Yesterday, ${format(parsedDate, TIME_FORMATS.TIME)}` : 'Yesterday';
  }

  return formatDate(parsedDate, includeTime ? DATE_FORMATS.DATETIME_MEDIUM : DATE_FORMATS.MEDIUM);
}

// Time format patterns
const TIME_FORMATS = {
  TIME: 'h:mm a',
};

/**
 * Get the start of a day
 */
export function getStartOfDay(date: Date | string = new Date()): Date
{
  const parsedDate = typeof date === 'string' ? parseISO(date) : date;
  return startOfDay(parsedDate);
}

/**
 * Get the end of a day
 */
export function getEndOfDay(date: Date | string = new Date()): Date
{
  const parsedDate = typeof date === 'string' ? parseISO(date) : date;
  return endOfDay(parsedDate);
}

/**
 * Get the start of a week
 */
export function getStartOfWeek(date: Date | string = new Date()): Date
{
  const parsedDate = typeof date === 'string' ? parseISO(date) : date;
  return startOfWeek(parsedDate, { weekStartsOn: 1 }); // Monday as first day
}

/**
 * Get the end of a week
 */
export function getEndOfWeek(date: Date | string = new Date()): Date
{
  const parsedDate = typeof date === 'string' ? parseISO(date) : date;
  return endOfWeek(parsedDate, { weekStartsOn: 1 });
}

/**
 * Get the start of a month
 */
export function getStartOfMonth(date: Date | string = new Date()): Date
{
  const parsedDate = typeof date === 'string' ? parseISO(date) : date;
  return startOfMonth(parsedDate);
}

/**
 * Get the end of a month
 */
export function getEndOfMonth(date: Date | string = new Date()): Date
{
  const parsedDate = typeof date === 'string' ? parseISO(date) : date;
  return endOfMonth(parsedDate);
}

/**
 * Get the start of a year
 */
export function getStartOfYear(date: Date | string = new Date()): Date
{
  const parsedDate = typeof date === 'string' ? parseISO(date) : date;
  return startOfYear(parsedDate);
}

/**
 * Get the end of a year
 */
export function getEndOfYear(date: Date | string = new Date()): Date
{
  const parsedDate = typeof date === 'string' ? parseISO(date) : date;
  return endOfYear(parsedDate);
}

/**
 * Add days to a date
 */
export function addDaysToDate(date: Date | string, days: number): Date
{
  const parsedDate = typeof date === 'string' ? parseISO(date) : date;
  return addDays(parsedDate, days);
}

/**
 * Subtract days from a date
 */
export function subtractDaysFromDate(date: Date | string, days: number): Date
{
  const parsedDate = typeof date === 'string' ? parseISO(date) : date;
  return subDays(parsedDate, days);
}

/**
 * Add months to a date
 */
export function addMonthsToDate(date: Date | string, months: number): Date
{
  const parsedDate = typeof date === 'string' ? parseISO(date) : date;
  return addMonths(parsedDate, months);
}

/**
 * Subtract months from a date
 */
export function subtractMonthsFromDate(date: Date | string, months: number): Date
{
  const parsedDate = typeof date === 'string' ? parseISO(date) : date;
  return subMonths(parsedDate, months);
}

/**
 * Get the difference in days between two dates
 */
export function getDaysDifference(date1: Date | string, date2: Date | string = new Date()): number
{
  const parsedDate1 = typeof date1 === 'string' ? parseISO(date1) : date1;
  const parsedDate2 = typeof date2 === 'string' ? parseISO(date2) : date2;
  return differenceInDays(parsedDate1, parsedDate2);
}

/**
 * Check if two dates are the same day
 */
export function isSameDayAs(date1: Date | string, date2: Date | string): boolean
{
  const parsedDate1 = typeof date1 === 'string' ? parseISO(date1) : date1;
  const parsedDate2 = typeof date2 === 'string' ? parseISO(date2) : date2;
  return isSameDay(parsedDate1, parsedDate2);
}

/**
 * Check if two dates are the same week
 */
export function isSameWeekAs(date1: Date | string, date2: Date | string): boolean
{
  const parsedDate1 = typeof date1 === 'string' ? parseISO(date1) : date1;
  const parsedDate2 = typeof date2 === 'string' ? parseISO(date2) : date2;
  return isSameWeek(parsedDate1, parsedDate2, { weekStartsOn: 1 });
}

/**
 * Check if two dates are the same month
 */
export function isSameMonthAs(date1: Date | string, date2: Date | string): boolean
{
  const parsedDate1 = typeof date1 === 'string' ? parseISO(date1) : date1;
  const parsedDate2 = typeof date2 === 'string' ? parseISO(date2) : date2;
  return isSameMonth(parsedDate1, parsedDate2);
}

/**
 * Check if a date is valid
 */
export function isDateValid(date: Date | string | null | undefined): boolean
{
  if (!date) return false;
  const parsedDate = typeof date === 'string' ? parseISO(date) : date;
  return isValid(parsedDate);
}

/**
 * Get the current date at the start of the day
 */
export function getTodayStart(): Date
{
  return startOfDay(new Date());
}

/**
 * Get the current date at the end of the day
 */
export function getTodayEnd(): Date
{
  return endOfDay(new Date());
}

/**
 * Count working days in an inclusive UTC date range, skipping weekends and holidays.
 * Holiday dates are matched by their YYYY-MM-DD prefix.
 */
export function countWorkingDays(from: Date, to: Date, holidays: { Date: string }[]): number
{
  const holidayKeys = new Set(holidays.map(h => h.Date.split('T')[0]));
  let days = 0;
  const cur = new Date(from);
  while (cur <= to)
  {
    const isWeekend = cur.getUTCDay() === 0 || cur.getUTCDay() === 6;
    const isHoliday = holidayKeys.has(cur.toISOString().split('T')[0]);
    if (!isWeekend && !isHoliday) days++;
    cur.setUTCDate(cur.getUTCDate() + 1);
  }
  return days;
}
